// Find Companies excludes (CLE-550): turn the sidebar's exclude state into the
// ``exclusions`` object the company endpoints take, and refuse what they would
// refuse before any request leaves.
//
// The excludes are not wire filters. ``buildCompanyFilters`` never sees them and
// ``hasAnyCompanyFilter`` does not count them. Pure and React-free, like
// ``people-exclusion-validation`` and ``cleanlist-exclusions.util`` on the
// people side.

import { normalizeDomain, normalizeDomainList } from '@/lib/normalize-domain';
import type {
  CompanyExclusionClause,
  CompanyExclusions,
  CompanyExclusionSummary,
} from '@/store/features/company-search.feature';
import type { CrmFilterClause, CrmFilterCrm } from '@/types/search-filters';
import type { CompanySearchFilters } from '@/utils/company-search.utils';
import { dedupeListIds } from '@/utils/cleanlist-exclusions.util';
import {
  conditionExcludesPeople,
  FIELD_PREFIX,
  isWellFormedCrmClause,
  parseFieldCondition,
} from '@/utils/crm-section-clauses.util';
import { FilterCompileError } from '@/utils/filter-compile-error.util';
import { BRAND } from '@/lib/brand';

const CRMS: readonly CrmFilterCrm[] = ['salesforce', 'hubspot'];

/** A registrable-looking hostname: dot-separated labels, no empty label, no
 *  leading or trailing hyphen. ``normalizeDomain`` has already lowercased it
 *  and turned an internationalized name into punycode. */
const HOSTNAME =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/** The domain an exclude input names, as the search will exclude it, or null
 *  when the text is no domain. The add sites use it so that text the search
 *  would refuse (``validateCompanyExclusions``) is never added as a pill. */
export function excludeDomain(raw: string): string | null {
  const domain = normalizeDomain(raw);
  return domain && HOSTNAME.test(domain) ? domain : null;
}

/** A saved search or link may predate these keys; read them defensively. */
function readSections(
  filters: CompanySearchFilters
): Record<CrmFilterCrm, unknown> {
  return {
    salesforce: filters.crmFilterSections?.salesforce ?? [],
    hubspot: filters.crmFilterSections?.hubspot ?? [],
  };
}

/** True when any exclude is selected, valid or not. An invalid selection is
 *  still a selection: it must be refused, never read as "nothing excluded". */
export function hasCompanyExclusions(filters: CompanySearchFilters): boolean {
  const sections = readSections(filters);
  return (
    (filters.excludeCompanyDomains ?? []).length > 0 ||
    (filters.excludeInCleanlist ?? 'off') !== 'off' ||
    CRMS.some(
      (crm) => !Array.isArray(sections[crm]) || sections[crm].length > 0
    )
  );
}

/** Refuse a selection the server would refuse, before any request: a CRM
 *  clause that is malformed, an inclusion, or one that excludes people; a list
 *  scope with no lists; a domain that is not one. An unsupported saved clause
 *  must stay visible instead of becoming a wider search. */
export function validateCompanyExclusions(filters: CompanySearchFilters): void {
  const sections = readSections(filters);
  for (const crm of CRMS) {
    const clauses = sections[crm];
    if (!Array.isArray(clauses) || !clauses.every(isWellFormedCrmClause)) {
      throw new FilterCompileError(
        'crmFilterSections',
        'CRM exclusions',
        'A selected CRM filter is invalid. Remove or correct it before searching'
      );
    }
    for (const clause of clauses) {
      if (clause.polarity === 'include') {
        throw new FilterCompileError(
          'crmFilterSections',
          'CRM inclusion',
          'This saved inclusion filter is not supported. Remove it before searching'
        );
      }
      const condition = clause.condition.trim();
      if (
        condition.startsWith(FIELD_PREFIX) &&
        !parseFieldCondition(condition)
      ) {
        throw new FilterCompileError(
          'crmFilterSections',
          'CRM exclusions',
          'A selected CRM filter is invalid. Remove or correct it before searching'
        );
      }
      if (conditionExcludesPeople(condition)) {
        throw new FilterCompileError(
          'crmFilterSections',
          'CRM exclusions',
          `"${condition}" excludes people, and Find Companies excludes accounts. Remove it before searching`
        );
      }
    }
  }

  const scope = filters.excludeInCleanlist ?? 'off';
  if (scope !== 'off' && scope !== 'any' && scope !== 'list') {
    throw new FilterCompileError(
      'excludeInCleanlist',
      `${BRAND.name} exclusions`,
      'Choose Off, any of my lists, or specific lists'
    );
  }
  if (
    scope === 'list' &&
    dedupeListIds(filters.excludeInCleanlistListIds ?? []).length === 0
  ) {
    throw new FilterCompileError(
      'excludeInCleanlistListIds',
      `${BRAND.name} exclusions`,
      'Choose at least one list or turn this exclusion off'
    );
  }

  for (const value of filters.excludeCompanyDomains ?? []) {
    if (typeof value === 'string' && value.trim() === '') continue;
    if (
      typeof value !== 'string' ||
      !HOSTNAME.test(normalizeDomain(value) ?? '')
    ) {
      throw new FilterCompileError(
        'excludeCompanyDomains',
        'Excluded domains',
        `"${String(value).trim()}" is not a valid domain.`
      );
    }
  }
}

/** The request's ``exclusions``, or undefined when nothing is excluded (scope
 *  off, no domains, no CRM clauses). Throws ``FilterCompileError`` for a
 *  selection ``validateCompanyExclusions`` refuses. Callers send it only while
 *  the exclusions flag is on. */
export function buildCompanyExclusions(
  filters: CompanySearchFilters,
  options: { refreshCrm?: boolean } = {}
): CompanyExclusions | undefined {
  validateCompanyExclusions(filters);
  const scope = filters.excludeInCleanlist ?? 'off';
  const domains = normalizeDomainList(filters.excludeCompanyDomains ?? []);
  const sections = readSections(filters) as Record<
    CrmFilterCrm,
    CrmFilterClause[]
  >;
  const clauses = (crm: CrmFilterCrm): CompanyExclusionClause[] =>
    sections[crm].map((clause) => ({
      id: clause.id,
      polarity: 'exclude',
      condition: clause.condition.trim(),
    }));
  const salesforce = clauses('salesforce');
  const hubspot = clauses('hubspot');
  if (
    scope === 'off' &&
    domains.length === 0 &&
    salesforce.length === 0 &&
    hubspot.length === 0
  )
    return undefined;
  return {
    excludeCompanyDomains: domains,
    excludeInCleanlist: scope,
    excludeInCleanlistListIds:
      scope === 'list'
        ? dedupeListIds(filters.excludeInCleanlistListIds ?? [])
        : [],
    crmFilterSections: { salesforce, hubspot },
    refreshCrm: options.refreshCrm ?? false,
  };
}

const EXCLUSION_ERROR_CODES = [
  'company_excludes_unavailable',
  'invalid_exclusions',
  'crm_not_connected',
  'exclusions_incomplete',
];

export interface CompanyExclusionError {
  code: string;
  message: string;
  field?: string;
}

/** The server's refusal of a company exclude, from an RTK Query error:
 *  `detail: { code, message, field? }` for 403 company_excludes_unavailable,
 *  400 invalid_exclusions and crm_not_connected, and 409 exclusions_incomplete.
 *  Null for any other error, which keeps its existing handling. */
export function companyExclusionError(
  error: unknown
): CompanyExclusionError | null {
  const detail = (error as { data?: { detail?: unknown } } | null)?.data
    ?.detail;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail))
    return null;
  const { code, message, field } = detail as Record<string, unknown>;
  if (typeof code !== 'string' || !EXCLUSION_ERROR_CODES.includes(code))
    return null;
  if (typeof message !== 'string' || !message.trim()) return null;
  return {
    code,
    message,
    field: typeof field === 'string' ? field : undefined,
  };
}

/** True when the server sent fewer excluded domains to the search than the
 *  exclusions named, so the search's total is an upper bound. */
export function exclusionsCutShort(
  summary: CompanyExclusionSummary | undefined
): boolean {
  return (
    Boolean(summary) && summary!.sent_to_search < summary!.excluded_domains
  );
}

export const COMPANY_EXCLUDES_UNAVAILABLE =
  "Company excludes aren't available yet";

/** Throws when a request sent excludes and the response has no
 *  ``exclusion_summary``: an API that ignores the field ran the search without
 *  them, so its rows must not be shown as excluded. The error has the server's
 *  own refusal shape (403 ``company_excludes_unavailable``), so every caller's
 *  ``companyExclusionError`` handling shows it. */
export function requireExclusionSummary(
  sent: CompanyExclusions | undefined,
  response: { exclusion_summary?: CompanyExclusionSummary }
): void {
  if (!sent || response.exclusion_summary) return;
  throw Object.assign(new Error(COMPANY_EXCLUDES_UNAVAILABLE), {
    status: 403,
    data: {
      detail: {
        code: 'company_excludes_unavailable',
        message: COMPANY_EXCLUDES_UNAVAILABLE,
      },
    },
  });
}
